/**
 * NOOBDE — Spin & Win
 *
 * Everything the merchant can change (labels, coupon codes, weighted
 * probabilities, colors, button text) lives in ONE config object per
 * section instance, read from the JSON <script> tag the Liquid section
 * renders. This file never needs to change when offers/copy/odds change —
 * only sections/spin-wheel.liquid (via the theme editor) does.
 *
 * This script never talks to Shopify's Discounts API. It only displays
 * coupon codes the merchant typed into the theme editor, which the
 * merchant is responsible for having already created in
 * Shopify Admin → Discounts.
 *
 * NOTE ON STRUCTURE: critical listeners (spin button click, email form
 * submit, unlock detection) are bound FIRST and wrapped in isolated
 * try/catch blocks, before any canvas-drawing code runs. This is
 * deliberate — canvas text measurement is the most failure-prone part of
 * this file across browsers, and a single uncaught exception used to be
 * able to silently prevent the spin button's click listener (previously
 * bound near the end of the function) from ever attaching. Keep new code
 * that touches the DOM/canvas below the "CORE INTERACTIVITY" section, not
 * above it.
 */
(function () {
  'use strict';

  // Temporary — safe to remove once the email-unlock flow is confirmed
  // working across a few real test runs. Every line is prefixed so it's
  // easy to find and strip later.
  var DEBUG = true;
  function log() {
    if (!DEBUG || !window.console) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[Spin&Win]');
    console.log.apply(console, args);
  }
  function logError(context, err) {
    if (!window.console) return;
    console.error('[Spin&Win] Error in ' + context + ':', err);
  }

  function initSpinWheel(root) {
    var sectionId = root.dataset.sectionId;
    log('init start, sectionId =', sectionId);

    var configEl = document.getElementById('spin-win-config-' + sectionId);
    if (!configEl) {
      logError('init', 'config script tag not found for section ' + sectionId);
      return;
    }

    var CONFIG;
    try {
      CONFIG = JSON.parse(configEl.textContent);
    } catch (err) {
      logError('config JSON.parse', err);
      return;
    }

    var segments = (CONFIG.segments || []).filter(function (s) {
      return s.label && s.weight > 0;
    });
    if (!segments.length) {
      logError('init', 'no valid segments in config — check wheel segment blocks in theme editor');
      return;
    }
    log('config parsed OK,', segments.length, 'segments, requireEmail =', CONFIG.requireEmail);

    // ---------- DOM references ----------
    var canvas = document.getElementById('spin-wheel-canvas-' + sectionId);
    var spinBtn = document.getElementById('spin-win-btn-' + sectionId);
    var overlay = document.getElementById('spin-win-overlay-' + sectionId);
    var confettiCanvas = document.getElementById('spin-win-confetti-' + sectionId);

    var emailGate = document.getElementById('spin-win-email-gate-' + sectionId);
    var lockHint = document.getElementById('spin-win-lock-hint-' + sectionId);
    var emailForm = emailGate ? emailGate.querySelector('.spin-win__email-form') : null;
    var emailInput = document.getElementById('spin-win-email-input-' + sectionId);
    var emailSubmitBtn = document.getElementById('spin-win-email-submit-' + sectionId);
    var emailErrorEl = document.getElementById('spin-win-email-error-' + sectionId);

    if (!spinBtn) {
      logError('init', 'spin button not found — id spin-win-btn-' + sectionId + ' missing from DOM');
      return;
    }
    if (!overlay) {
      logError('init', 'result overlay not found — id spin-win-overlay-' + sectionId + ' missing from DOM');
      return;
    }

    var STORAGE_KEY = 'noobde_spin_win_used_' + sectionId;
    var EMAIL_KEY = 'noobde_spin_win_email_' + sectionId;
    var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    var currentRotation = 0; // radians, cumulative — shared with the drawing code below
    var spinning = false;

    // ============================================================
    // CORE INTERACTIVITY — unlock detection + button wiring.
    // Bound first, each step isolated, so canvas/drawing issues
    // further down can never prevent these from working.
    // ============================================================

    function isUnlocked() {
      var stored = null;
      try { stored = localStorage.getItem(EMAIL_KEY); } catch (e) { /* storage unavailable */ }
      return !CONFIG.requireEmail || !!stored;
    }

    function unlockSpin(skipAnimation) {
      log('unlockSpin called, skipAnimation =', skipAnimation);
      spinBtn.disabled = false;
      spinBtn.removeAttribute('aria-disabled');
      if (lockHint) {
        lockHint.textContent = CONFIG.emailUnlockedHint || "You're unlocked — good luck!";
        lockHint.classList.add('spin-win--unlocked');
      }
      if (emailGate) {
        if (skipAnimation) {
          emailGate.style.display = 'none';
        } else {
          emailGate.classList.add('spin-win--fading');
          setTimeout(function () { emailGate.style.display = 'none'; }, 380);
        }
      }
      log('spin button disabled attribute is now', spinBtn.disabled);
    }

    function showEmailError(message) {
      if (!emailErrorEl) return;
      emailErrorEl.textContent = message;
      emailErrorEl.hidden = false;
      if (emailInput) emailInput.classList.add('spin-win--error');
    }

    function clearEmailError() {
      if (!emailErrorEl) return;
      emailErrorEl.hidden = true;
      if (emailInput) emailInput.classList.remove('spin-win--error');
    }

    // Step: returning visitor in this browser who already unlocked.
    try {
      if (CONFIG.requireEmail && isUnlocked()) {
        log('returning visitor detected via localStorage, skipping gate');
        unlockSpin(true);
      } else {
        log('gate check: requireEmail =', CONFIG.requireEmail, ', isUnlocked =', isUnlocked());
      }
    } catch (err) {
      logError('returning-visitor unlock check', err);
    }

    // Step: Shopify redirected back here after form.posted_successfully?
    try {
      var successMarker = document.getElementById('spin-win-email-success-' + sectionId);
      log('success marker element found?', !!successMarker);
      if (successMarker) {
        try {
          localStorage.setItem(EMAIL_KEY, successMarker.dataset.unlockedEmail || 'true');
          log('EMAIL_KEY saved to localStorage');
        } catch (storageErr) {
          logError('localStorage.setItem (non-fatal, unlock still proceeds)', storageErr);
        }
        unlockSpin(true);
      }
    } catch (err) {
      logError('success-marker unlock check', err);
    }

    // Step: email form submit — client-side format check, then native submit.
    try {
      if (emailForm && emailInput && emailSubmitBtn) {
        emailForm.addEventListener('submit', function (e) {
          log('email form submitted, value =', emailInput.value);
          var email = (emailInput.value || '').trim();
          if (!EMAIL_RE.test(email)) {
            e.preventDefault();
            log('email failed client-side validation');
            showEmailError(CONFIG.emailInvalidMessage || 'Please enter a valid email address.');
            emailInput.focus();
            return;
          }
          log('email passed validation, submitting natively to Shopify');
          clearEmailError();
          emailSubmitBtn.disabled = true;
          emailSubmitBtn.textContent = CONFIG.emailSubmittingLabel || 'Unlocking…';
          // No preventDefault from here — browser submits natively.
        });
      } else {
        log('email form elements not all present (form/input/submit) — gate may be disabled in settings, this is OK if so');
      }
    } catch (err) {
      logError('email form listener setup', err);
    }

    // Step: spin button click — this is the listener that was previously
    // getting silently skipped if anything above it threw. It's now bound
    // immediately after the DOM refs are confirmed to exist, decoupled
    // from all canvas/drawing code below.
    try {
      spinBtn.addEventListener('click', function () {
        log('spin button clicked, spinning =', spinning, ', unlocked =', isUnlocked());
        spin();
      });
      log('spin button click listener attached successfully');
    } catch (err) {
      logError('spin button listener attachment', err);
    }

    // ============================================================
    // WHEEL DRAWING + RESULT POPUP — everything below this point
    // is wrapped so failures here can't affect the interactivity
    // wired above.
    // ============================================================

    var ctx, size, radius, sliceAngle;
    var popup, popupTitle, popupMessage, couponBox, couponCodeEl, copyBtn, closeBtn, ribbonsHost;

    try {
      ctx = canvas.getContext('2d');
      size = canvas.width;
      radius = size / 2;
      sliceAngle = (Math.PI * 2) / segments.length;

      popup = overlay.querySelector('.spin-win__popup');
      popupTitle = overlay.querySelector('.spin-win__popup-title');
      popupMessage = overlay.querySelector('.spin-win__popup-message');
      couponBox = overlay.querySelector('.spin-win__coupon-box');
      couponCodeEl = overlay.querySelector('.spin-win__coupon-code');
      copyBtn = overlay.querySelector('.spin-win__copy-btn');
      closeBtn = document.getElementById('spin-win-close-' + sectionId);
      ribbonsHost = overlay.querySelector('.spin-win__popup-ribbons');
    } catch (err) {
      logError('popup/canvas DOM setup', err);
    }

    function drawWheel() {
      try {
        ctx.clearRect(0, 0, size, size);
        segments.forEach(function (seg, i) {
          var start = i * sliceAngle;
          var end = start + sliceAngle;

          ctx.beginPath();
          ctx.moveTo(radius, radius);
          ctx.arc(radius, radius, radius, start, end);
          ctx.closePath();
          ctx.fillStyle = seg.color || '#eee';
          ctx.fill();
          ctx.strokeStyle = 'rgba(255,255,255,0.6)';
          ctx.lineWidth = 2;
          ctx.stroke();

          ctx.save();
          ctx.translate(radius, radius);
          ctx.rotate(start + sliceAngle / 2);
          ctx.textAlign = 'right';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = getReadableTextColor(seg.color);
          ctx.font = '600 20px sans-serif';
          wrapRadialText(ctx, seg.label, radius - 24, 0, 90);
          ctx.restore();
        });
        log('wheel drawn successfully');
      } catch (err) {
        logError('drawWheel', err);
      }
    }

    function wrapRadialText(context, text, x, y, maxWidth) {
      var words = text.split(' ');
      var lines = [];
      var line = '';
      words.forEach(function (word) {
        var test = line ? line + ' ' + word : word;
        if (context.measureText(test).width > maxWidth && line) {
          lines.push(line);
          line = word;
        } else {
          line = test;
        }
      });
      if (line) lines.push(line);
      lines.forEach(function (l, idx) {
        context.fillText(l, x, y + (idx - (lines.length - 1) / 2) * 20);
      });
    }

    function getReadableTextColor(hex) {
      if (!hex) return '#111';
      var c = hex.replace('#', '');
      if (c.length === 3) c = c.split('').map(function (ch) { return ch + ch; }).join('');
      var r = parseInt(c.substr(0, 2), 16);
      var g = parseInt(c.substr(2, 2), 16);
      var b = parseInt(c.substr(4, 2), 16);
      var luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.6 ? '#111111' : '#ffffff';
    }

    drawWheel();

    function pickSegment() {
      var total = segments.reduce(function (sum, s) { return sum + s.weight; }, 0);
      var roll = Math.random() * total;
      var acc = 0;
      for (var i = 0; i < segments.length; i++) {
        acc += segments[i].weight;
        if (roll <= acc) return i;
      }
      return segments.length - 1;
    }

    function spin() {
      if (spinning) {
        log('spin() called but already spinning — ignored');
        return;
      }
      if (!isUnlocked()) {
        log('spin() called but not unlocked — focusing email input');
        if (emailInput) emailInput.focus();
        return;
      }
      if (CONFIG.limitOneSpin && localStorage.getItem(STORAGE_KEY)) {
        log('spin already used this browser — replaying stored result');
        showResult(getStoredResult());
        return;
      }

      spinning = true;
      spinBtn.disabled = true;
      spinBtn.textContent = CONFIG.spinningLabel || 'Spinning…';

      var winnerIndex = pickSegment();
      var winner = segments[winnerIndex];
      log('spinning — winner will be', winner.label, '(outcome:', winner.outcome + ')');

      var winnerMidAngle = winnerIndex * sliceAngle + sliceAngle / 2;
      var extraSpins = 6 + Math.floor(Math.random() * 3);
      var targetRotation =
        currentRotation +
        extraSpins * Math.PI * 2 +
        (Math.PI * 1.5 - winnerMidAngle - (currentRotation % (Math.PI * 2)));

      animateTo(targetRotation, 5200, function () {
        currentRotation = targetRotation;
        spinning = false;
        spinBtn.textContent = CONFIG.spinButtonLabel || 'SPIN';
        if (CONFIG.limitOneSpin) {
          storeResult(winner);
        }
        notifySpinCompleted();
        showResult(winner);
      });
    }

    function animateTo(targetRadians, duration, done) {
      var startRotation = currentRotation;
      var delta = targetRadians - startRotation;
      var startTime = null;

      function easeOutCubic(t) {
        return 1 - Math.pow(1 - t, 3);
      }

      function frame(ts) {
        if (!startTime) startTime = ts;
        var elapsed = ts - startTime;
        var t = Math.min(elapsed / duration, 1);
        var eased = easeOutCubic(t);
        var rotation = startRotation + delta * eased;
        canvas.style.transform = 'rotate(' + rotation + 'rad)';
        if (t < 1) {
          requestAnimationFrame(frame);
        } else {
          done();
        }
      }
      requestAnimationFrame(frame);
    }

    function storeResult(winner) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(winner));
      } catch (e) { /* storage unavailable — spin limit just won't persist */ }
    }
    function getStoredResult() {
      try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY));
      } catch (e) {
        return null;
      }
    }

    function notifySpinCompleted() {
      if (!CONFIG.sendCompletionTag || !emailForm) return;
      var email = null;
      try { email = localStorage.getItem(EMAIL_KEY); } catch (e) { /* ignore */ }
      if (!email || email === 'true') {
        email = (emailInput && emailInput.value) || null;
      }
      if (!email) return;

      var formData = new FormData(emailForm);
      formData.set('contact[email]', email);
      formData.set('contact[tags]', CONFIG.completionTag || 'spin-completed');

      fetch(emailForm.action, { method: 'POST', body: formData }).catch(function (err) {
        logError('notifySpinCompleted background POST (non-fatal)', err);
      });
    }

    function showResult(winner) {
      if (!winner) return;
      var isWin = winner.outcome === 'win' && winner.coupon;

      if (popup) popup.classList.toggle('spin-win--lose', !isWin);
      if (popupTitle) popupTitle.textContent = isWin ? CONFIG.winPopupTitle : CONFIG.losePopupTitle;

      if (isWin) {
        var msg = (CONFIG.winMessageTemplate || 'Use code {code} at checkout.').replace('{code}', winner.coupon);
        if (popupMessage) popupMessage.textContent = msg;
        if (couponBox) couponBox.hidden = false;
        if (couponCodeEl) couponCodeEl.textContent = winner.coupon;
      } else {
        if (popupMessage) popupMessage.textContent = CONFIG.loseMessage || "Better luck next time!";
        if (couponBox) couponBox.hidden = true;
      }

      overlay.hidden = false;

      if (isWin && CONFIG.confettiEnabled) {
        fireConfetti();
      }
      fireRibbons(isWin ? (winner.color || '#a3e635') : '#cccccc');
    }

    function hideResult() {
      overlay.hidden = true;
    }

    try {
      if (closeBtn) closeBtn.addEventListener('click', hideResult);
      overlay.addEventListener('click', function (e) {
        if (e.target === overlay) hideResult();
      });
      if (copyBtn) {
        copyBtn.addEventListener('click', function () {
          var code = couponCodeEl ? couponCodeEl.textContent : '';
          var restoreLabel = CONFIG.copyButtonLabel || 'Copy Coupon';
          function flashCopied() {
            copyBtn.textContent = CONFIG.copiedLabel || 'Copied!';
            setTimeout(function () { copyBtn.textContent = restoreLabel; }, 1800);
          }
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(code).then(flashCopied).catch(function () {
              fallbackCopy(code, flashCopied);
            });
          } else {
            fallbackCopy(code, flashCopied);
          }
        });
      }
    } catch (err) {
      logError('popup listener setup', err);
    }

    function fallbackCopy(text, done) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (e) { /* no-op */ }
      document.body.removeChild(ta);
      done();
    }

    function fireRibbons(baseColor) {
      if (!ribbonsHost) return;
      ribbonsHost.innerHTML = '';
      var colors = [baseColor, '#ffffff', '#111111', '#a3e635'];
      var count = 18;
      for (var i = 0; i < count; i++) {
        var ribbon = document.createElement('span');
        ribbon.className = 'spin-win__ribbon';
        ribbon.style.setProperty('--sw-r', (i * (360 / count)) + 'deg');
        ribbon.style.background = colors[i % colors.length];
        ribbon.style.animationDelay = (Math.random() * 0.15) + 's';
        ribbonsHost.appendChild(ribbon);
      }
      setTimeout(function () { ribbonsHost.innerHTML = ''; }, 1200);
    }

    function fireConfetti() {
      if (!confettiCanvas) return;
      var cctx = confettiCanvas.getContext('2d');
      var dpr = window.devicePixelRatio || 1;
      confettiCanvas.width = window.innerWidth * dpr;
      confettiCanvas.height = window.innerHeight * dpr;
      confettiCanvas.style.width = window.innerWidth + 'px';
      confettiCanvas.style.height = window.innerHeight + 'px';
      cctx.scale(dpr, dpr);

      var colors = ['#a3e635', '#f4d35e', '#ee6c4d', '#3d5a80', '#ffffff'];
      var particles = [];
      var count = 140;
      var originX = window.innerWidth / 2;
      var originY = window.innerHeight / 2;

      for (var i = 0; i < count; i++) {
        var angle = Math.random() * Math.PI * 2;
        var speed = 4 + Math.random() * 8;
        particles.push({
          x: originX,
          y: originY,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - 4,
          size: 5 + Math.random() * 5,
          color: colors[Math.floor(Math.random() * colors.length)],
          rotation: Math.random() * Math.PI,
          rotationSpeed: (Math.random() - 0.5) * 0.3,
          gravity: 0.18,
          life: 0
        });
      }

      var maxLife = 90;
      function tick() {
        cctx.clearRect(0, 0, confettiCanvas.width, confettiCanvas.height);
        var alive = false;
        particles.forEach(function (p) {
          if (p.life >= maxLife) return;
          alive = true;
          p.vy += p.gravity;
          p.x += p.vx;
          p.y += p.vy;
          p.rotation += p.rotationSpeed;
          p.life++;

          cctx.save();
          cctx.globalAlpha = Math.max(0, 1 - p.life / maxLife);
          cctx.translate(p.x, p.y);
          cctx.rotate(p.rotation);
          cctx.fillStyle = p.color;
          cctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
          cctx.restore();
        });
        if (alive) {
          requestAnimationFrame(tick);
        } else {
          cctx.clearRect(0, 0, confettiCanvas.width, confettiCanvas.height);
        }
      }
      requestAnimationFrame(tick);
    }

    log('init complete for section', sectionId);
  }

  function init() {
    document.querySelectorAll('.spin-win').forEach(function (root) {
      try {
        initSpinWheel(root);
      } catch (err) {
        logError('initSpinWheel (top-level)', err);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
